import React from 'react';
import { useTranslation } from 'react-i18next';
import { Store, Truck } from 'lucide-react';
import type { OnlineOrder } from '@/services/types';
import { Button } from '@/components/ui';
import { formatCurrency, formatRelativeShort } from '@/utils/formatters';
import { isPickup, type OnlineOrderActions } from './useOnlineOrderActions';

export const orderItemsSummary = (o: OnlineOrder) => o.transactionsItems.map((it) => `${it.quantity}× ${it.products?.title ?? '-'}`).join(', ');
export const orderAgo = (iso: string, lang: string) => formatRelativeShort(iso, lang);

/** O8 online order card with the stage action. */
export const OrderCard: React.FC<{ order: OnlineOrder; actions: OnlineOrderActions; onOpen: () => void }> = ({ order, actions, onOpen }) => {
    const { t, i18n } = useTranslation();
    const pickup = isPickup(order);
    const Icon = pickup ? Store : Truck;
    const items = actions.actionsFor(order);
    return (
        <div role="button" tabIndex={0} onClick={onOpen} onKeyDown={(e) => e.key === 'Enter' && onOpen()} className="flex cursor-pointer flex-col gap-2 rounded-lg border border-line-subtle bg-surface-card p-3.5 shadow-sm hover:shadow-md lg:p-3.5">
            <div className="flex items-center gap-2">
                <span className="min-w-0 flex-1 truncate text-[15px] font-black">{order.recipientName || order.users?.fullname || '-'}</span>
                <span className="shrink-0 text-xs text-ink-muted">{orderAgo(order.createdAt, i18n.language)}</span>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-ink-muted">
                <span className="font-extrabold text-ink-secondary">{order.transactionId}</span>·<Icon className="h-3.5 w-3.5" />
                <span className="truncate">{pickup ? t('onlineOrders.pickup') : [t('onlineOrders.courier'), [order.courierName, order.courierService].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}</span>
            </div>
            <p className="line-clamp-2 text-[13px] leading-normal text-ink-secondary">{orderItemsSummary(order)}</p>
            <div className="flex items-center justify-between gap-2 border-t border-line-subtle pt-2">
                <span className="text-base font-black tnum">{formatCurrency(order.total)}</span>
                {items[0] && <Button size="sm" variant={items[0].variant} onClick={(e) => { e.stopPropagation(); actions.request(order, items[0].action); }}>{items[0].label}</Button>}
            </div>
        </div>
    );
};
export default OrderCard;
